import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X, Pencil } from "lucide-react";
import { API, logErr, inr, fmtErr } from "../../lib/api";

const EMPTY = { code: "", type: "percent", value: "", min_order: "", active: true };

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(() => API.get("/admin/coupons").then((r) => setCoupons(r.data)).catch((err) => logErr("Failed to load coupons:", err)), [API, logErr]);
  useEffect(() => { load(); document.title = "Coupons | Admin"; }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { code: form.code, type: form.type, value: parseFloat(form.value),
        min_order: parseFloat(form.min_order || "0"), active: !!form.active,
        expires_at: new Date(Date.now() + 365 * 864e5).toISOString() };
      if (editing?.id) await API.put(`/admin/coupons/${editing.id}`, payload);
      else await API.post("/admin/coupons", payload);
      toast.success("Coupon saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const del = async (id) => {
    await API.delete(`/admin/coupons/${id}`);
    toast.success("Deleted");
    load();
  };

  const inputCls = "w-full bg-ink border border-[#262626] px-3 py-2.5 text-sm outline-none focus:border-gold transition-colors";

  if (!coupons) return <div className="text-muted-foreground">Loading...</div>;

  return (
    <div data-testid="admin-coupons">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display font-black text-3xl tracking-tighter">Coupons</h1>
        <button onClick={() => { setForm(EMPTY); setEditing({}); }} data-testid="new-coupon-btn"
          className="btn-gold px-5 py-3 text-xs font-bold uppercase tracking-widest flex items-center gap-2">
          <Plus size={14} /> New coupon
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {coupons.map((c) => (
          <div key={c.id} className="border border-dashed border-gold/40 bg-ink-surface p-5 relative" data-testid={`coupon-${c.code}`}>
            <p className="font-display font-black text-xl gold-text tracking-widest">{c.code}</p>
            <p className="text-sm mt-2">{c.type === "percent" ? `${c.value}% off` : `${inr(c.value)} off`}</p>
            <p className="text-xs text-muted-foreground mt-1">Min order {inr(c.min_order)}</p>
            <span className={`absolute top-4 right-4 text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 ${c.active ? "bg-green-400/10 text-green-400" : "bg-red-400/10 text-red-400"}`}>
              {c.active ? "Active" : "Off"}
            </span>
            <div className="flex gap-2 mt-4">
              <button onClick={() => { setForm({ code: c.code, type: c.type, value: String(c.value), min_order: String(c.min_order), active: c.active }); setEditing(c); }}
                data-testid={`edit-coupon-${c.code}`} className="text-muted-foreground hover:text-gold"><Pencil size={14} /></button>
              <button onClick={() => del(c.id)} data-testid={`delete-coupon-${c.code}`} className="text-muted-foreground hover:text-red-400"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="coupon-form-modal">
          <form onSubmit={save} className="bg-ink-surface border border-[#262626] w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-bold text-xl">{editing?.id ? "Edit coupon" : "New coupon"}</h2>
              <button type="button" onClick={() => setEditing(null)}><X size={18} /></button>
            </div>
            <input data-testid="cp-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} required placeholder="CODE" className={`${inputCls} uppercase`} />
            <select data-testid="cp-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={inputCls}>
              <option value="percent">Percent off</option>
              <option value="flat">Flat amount off</option>
            </select>
            <input data-testid="cp-value" type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} required placeholder={form.type === "percent" ? "Percent (e.g. 10)" : "Amount (e.g. 500)"} className={inputCls} />
            <input data-testid="cp-min" type="number" value={form.min_order} onChange={(e) => setForm({ ...form, min_order: e.target.value })} placeholder="Minimum order (₹)" className={inputCls} />
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#D4AF37]" data-testid="cp-active" /> Active
            </label>
            <button data-testid="cp-save-btn" className="btn-gold w-full py-3.5 text-xs font-bold uppercase tracking-[0.2em]">Save coupon</button>
          </form>
        </div>
      )}
    </div>
  );
}
